// Customisable, org-branded emails (PTEC-895).
//
// Mirrors the backend definitions and validator in
// settingsLayer/lib/nodejs/emailTemplates (definitions.js, validate.js). A browser
// bundle cannot import a Lambda layer, so the rules are duplicated here by
// necessity. The API is authoritative: it re-validates every write and its
// `errors[]` are mapped back onto the fields, so drift here costs a round trip,
// never a bad email.

/** Platform feature flag. Default off: pages and nav items are hidden until it is on. */
export const CUSTOM_EMAILS_FLAG = 'custom-emails-enabled';

export const EMAIL_TEMPLATE_KEYS = [
	'invite',
	'org-member-added',
	'partner-invitation',
	'signup-verification',
	'password-reset',
	'lost-mfa',
	'documents-shared'
] as const;

export type EmailTemplateKey = (typeof EMAIL_TEMPLATE_KEYS)[number];

/** The only templates an organisation may override. */
export const ORG_OVERRIDABLE_TEMPLATE_KEYS: EmailTemplateKey[] = ['invite', 'org-member-added', 'partner-invitation'];

/**
 * Templates sent through the Cognito CustomMessage trigger, which rejects messages
 * over 20,000 characters. The backend keeps a margin for the code and username
 * Cognito substitutes afterwards.
 */
export const COGNITO_TEMPLATE_KEYS: EmailTemplateKey[] = ['invite', 'signup-verification', 'password-reset'];
export const MAX_COGNITO_LENGTH = 19500;

export type EmailPlaceholder = 'orgName' | 'appName' | 'firstName' | 'expiryDays' | 'senderName';

export interface EmailTemplateMeta {
	orgOverridable: boolean;
	hasButton: boolean;
	placeholders: EmailPlaceholder[];
}

/**
 * Fallback metadata, used only when the API response omits it. The API returns
 * `hasButton` and `placeholders` for every template and those win.
 */
export const EMAIL_TEMPLATE_META: Record<EmailTemplateKey, EmailTemplateMeta> = {
	invite: {
		orgOverridable: true,
		hasButton: true,
		placeholders: ['orgName', 'appName', 'firstName', 'expiryDays']
	},
	'org-member-added': {
		orgOverridable: true,
		hasButton: true,
		placeholders: ['orgName', 'appName', 'firstName']
	},
	'partner-invitation': {
		orgOverridable: true,
		hasButton: true,
		placeholders: ['orgName', 'appName', 'firstName']
	},
	'signup-verification': {
		orgOverridable: false,
		hasButton: false,
		placeholders: ['orgName', 'appName']
	},
	'password-reset': {
		orgOverridable: false,
		hasButton: false,
		placeholders: ['orgName', 'appName', 'firstName']
	},
	'lost-mfa': {
		orgOverridable: false,
		hasButton: true,
		placeholders: ['orgName', 'appName', 'firstName']
	},
	'documents-shared': {
		orgOverridable: false,
		hasButton: true,
		placeholders: ['orgName', 'appName', 'firstName', 'senderName']
	}
};

export const isEmailTemplateKey = (value: string): value is EmailTemplateKey =>
	(EMAIL_TEMPLATE_KEYS as readonly string[]).includes(value);

// ------------------------------------------------------------------ values

export type EmailTextField = 'subject' | 'preheader' | 'heading' | 'body' | 'buttonText' | 'signOff';

export interface EmailHelpLink {
	label: string;
	url: string;
}

/**
 * A stored (or draft) template. An absent field inherits; `helpLink: null` means
 * "no help link", which is different from absent.
 */
export type EmailTemplateValue = Partial<Record<EmailTextField, string>> & {
	helpLink?: EmailHelpLink | null;
};

/** Stored branding. `headerTitle` may be '' (the co-branded header then has no title line). */
export interface EmailBrandingValue {
	headerTitle?: string;
	primaryColour?: string;
}

export interface EmailTextFieldRule {
	max: number;
	required: boolean;
	multiline: boolean;
	buttonOnly?: boolean;
}

// Order is the order the fields render in.
export const EMAIL_FIELD_RULES: Record<EmailTextField, EmailTextFieldRule> = {
	subject: { max: 120, required: true, multiline: false },
	preheader: { max: 140, required: false, multiline: false },
	heading: { max: 120, required: true, multiline: false },
	body: { max: 3000, required: true, multiline: true },
	buttonText: { max: 40, required: true, multiline: false, buttonOnly: true },
	signOff: { max: 300, required: true, multiline: true }
};

export const HELP_LINK_LIMITS = { labelMax: 60, urlMax: 500 } as const;

export const BRANDING_LIMITS = { headerTitleMax: 60, minContrast: 4.5 } as const;

/** Placeholders usable in the branding header title. */
export const BRANDING_PLACEHOLDERS: EmailPlaceholder[] = ['orgName', 'appName'];

export const HEX_COLOUR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** `{{name}}`. Global: use with `matchAll` / `replace`, never `test` (lastIndex is stateful). */
export const PLACEHOLDER_PATTERN = /\{\{(\w+)\}\}/g;

/** The text fields a template has, in render order. */
export const emailFieldsFor = (hasButton: boolean): EmailTextField[] =>
	(Object.keys(EMAIL_FIELD_RULES) as EmailTextField[]).filter(
		(field) => hasButton || !EMAIL_FIELD_RULES[field].buttonOnly
	);

// ------------------------------------------------------------------ contrast

const channel = (value: number) => {
	const c = value / 255;
	return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

const luminance = (hex: string) => {
	const n = parseInt(hex.slice(1), 16);
	return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
};

/**
 * WCAG 2.x contrast ratio between two `#RRGGBB` colours (white text by default).
 * Returns NaN for anything that is not a six-digit hex colour.
 */
export const contrastRatio = (hex: string, against = '#FFFFFF'): number => {
	if (!HEX_COLOUR_PATTERN.test(hex) || !HEX_COLOUR_PATTERN.test(against)) {
		return NaN;
	}
	const [light, dark] = [luminance(hex), luminance(against)].sort((a, b) => b - a);
	return (light + 0.05) / (dark + 0.05);
};

// ------------------------------------------------------------------ validation

export type EmailFieldErrorCode =
	| 'REQUIRED'
	| 'TOO_LONG'
	| 'SINGLE_LINE'
	| 'INVALID_PLACEHOLDER'
	| 'INVALID_URL'
	| 'INVALID_COLOUR'
	| 'LOW_CONTRAST';

export interface EmailFieldError {
	/** Field path as the API reports it, e.g. 'subject', 'helpLink.url', 'primaryColour'. */
	field: string;
	code: EmailFieldErrorCode;
	/** Interpolation values for the message (e.g. `max`, `ratio`). */
	params?: Record<string, string | number>;
}

export const placeholdersIn = (text: string): string[] => [...text.matchAll(PLACEHOLDER_PATTERN)].map((m) => m[1]);

const isHttpsUrl = (value: string) => {
	try {
		return new URL(value).protocol === 'https:';
	} catch {
		return false;
	}
};

const checkText = (
	field: string,
	value: string,
	rule: { max: number; required: boolean; multiline: boolean },
	allowed: readonly string[],
	errors: EmailFieldError[]
) => {
	if (rule.required && value.trim() === '') {
		errors.push({ field, code: 'REQUIRED' });
	}
	if (value.length > rule.max) {
		errors.push({ field, code: 'TOO_LONG', params: { max: rule.max } });
	}
	if (!rule.multiline && /[\r\n]/.test(value)) {
		errors.push({ field, code: 'SINGLE_LINE' });
	}
	// After removing the allowed placeholders no brace may remain: this blocks
	// Cognito's `{####}` / `{username}` and any unknown `{{name}}`.
	const unknown = placeholdersIn(value).filter((name) => !allowed.includes(name));
	if (unknown.length || /[{}]/.test(value.replace(PLACEHOLDER_PATTERN, ''))) {
		errors.push({ field, code: 'INVALID_PLACEHOLDER' });
	}
};

/**
 * Validates a template draft with the §8.3 rules. Only fields that are present are
 * checked: absent fields (and a `null` text field) inherit.
 */
export const validateTemplateDraft = (
	value: EmailTemplateValue,
	{ hasButton, placeholders }: { hasButton: boolean; placeholders: readonly string[] }
): EmailFieldError[] => {
	const errors: EmailFieldError[] = [];
	emailFieldsFor(hasButton).forEach((field) => {
		const current = value[field];
		if (typeof current === 'string') {
			checkText(field, current, EMAIL_FIELD_RULES[field], placeholders, errors);
		}
	});
	const link = value.helpLink;
	if (link) {
		checkText(
			'helpLink.label',
			link.label ?? '',
			{ max: HELP_LINK_LIMITS.labelMax, required: true, multiline: false },
			placeholders,
			errors
		);
		const url = link.url ?? '';
		if (url.length > HELP_LINK_LIMITS.urlMax || !isHttpsUrl(url)) {
			errors.push({ field: 'helpLink.url', code: 'INVALID_URL' });
		}
	}
	return errors;
};

/** Validates a branding draft. Absent fields inherit. */
export const validateBrandingDraft = (value: EmailBrandingValue): EmailFieldError[] => {
	const errors: EmailFieldError[] = [];
	if (typeof value.headerTitle === 'string') {
		checkText(
			'headerTitle',
			value.headerTitle,
			{ max: BRANDING_LIMITS.headerTitleMax, required: false, multiline: false },
			BRANDING_PLACEHOLDERS,
			errors
		);
	}
	if (typeof value.primaryColour === 'string') {
		if (!HEX_COLOUR_PATTERN.test(value.primaryColour)) {
			errors.push({ field: 'primaryColour', code: 'INVALID_COLOUR' });
		} else {
			const ratio = contrastRatio(value.primaryColour);
			if (ratio < BRANDING_LIMITS.minContrast) {
				errors.push({
					field: 'primaryColour',
					code: 'LOW_CONTRAST',
					params: { ratio: ratio.toFixed(2), min: BRANDING_LIMITS.minContrast }
				});
			}
		}
	}
	return errors;
};

/**
 * The value as it is stored and sent: keys set to `undefined` (inheriting) are
 * dropped, so they are absent rather than serialised.
 */
export const withoutInherited = <T extends object>(value: T): T => JSON.parse(JSON.stringify(value ?? {})) as T;
